import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'
import { PlanType } from '@/utils/planType'

/**
 * 契約情報取得
 */
import { ApiTaCm010 } from '~/endpoints/ApiTaCm010'

/**
 * コース・プランタイプ
 */
export interface CoursePlanContractInfo {
  juniorTabletCourse: boolean // 小学コース
  totalGuidancePlan: boolean // 中学受験コース(トータル指導プラン)
  cramSchoolLearningPlan: boolean // 中学受験コース(塾併用要点学習プラン)
  juniorNCourse: boolean // N本科
  aCourseAPlan: boolean // 頻出分野別演習 国語 超長文
  aCourseBPlan: boolean // 頻出分野別演習 国語 難関記述
  aCourseCPlan: boolean // 頻出分野別演習 算数 立体図形難問
  aCourseDPlan: boolean // 頻出分野別演習 算数 平面図形難問
  aCourseEPlan: boolean // 頻出分野別演習 理科 論理的思考問題
  aCourseFPlan: boolean // 頻出分野別演習 理科 複雑計算問題
  aCourseGPlan: boolean // 頻出分野別演習 社会 文章記述
  aCourseHPlan: boolean // 志望校別予想演習 筑波大附属駒場
  aCourseIPlan: boolean // 志望校別予想演習 開成
  aCourseJPlan: boolean // 志望校別予想演習 桜蔭
  aCourseKPlan: boolean // 志望校別予想演習 神戸女学院
  aCourseLPlan: boolean // 志望校別予想演習 灘
  aCourseMPlan: boolean // 志望校別予想演習 甲陽学院
  nCourseWPlan: boolean // 思考・表現力
  nCourseQPlan: boolean // 作文
  nCourseVPlan: boolean // 公立中高一貫校適性検査
  nCourseRPlan: boolean // 公立中高一貫校作文
  nCourse13Plan: boolean // N本科（5教科セット）
  nCourse14Plan: boolean // N本科（4教科セット）
}

export interface ContractInfoYyMm {
  materialYyMm: number
  subjectId: string
  isDeliveried: number
  logicalYear: number
}

export interface ContractInfo {
  courseId: string
  planId: string
  levelId: string
  yyMm: Array<ContractInfoYyMm>
}

export interface StateContractInformation {
  currentCourseId: string
  contractInfo: Array<ContractInfo>
  isContractInfo: number
  planTypes: Set<PlanType>
  coursePlanContractInfo: CoursePlanContractInfo
  statusCode: number
  error: {
    messageId: string
    message: string
  } | null
}
export const getEmptyStateContractInformation = (): StateContractInformation => ({
  currentCourseId: '',
  contractInfo: [],
  isContractInfo: 0,
  planTypes: new Set<PlanType>(),
  coursePlanContractInfo: {
    juniorTabletCourse: false,
    totalGuidancePlan: false,
    cramSchoolLearningPlan: false,
    juniorNCourse: false,
    aCourseAPlan: false,
    aCourseBPlan: false,
    aCourseCPlan: false,
    aCourseDPlan: false,
    aCourseEPlan: false,
    aCourseFPlan: false,
    aCourseGPlan: false,
    aCourseHPlan: false,
    aCourseIPlan: false,
    aCourseJPlan: false,
    aCourseKPlan: false,
    aCourseLPlan: false,
    aCourseMPlan: false,
    nCourseWPlan: false,
    nCourseQPlan: false,
    nCourseVPlan: false,
    nCourseRPlan: false,
    nCourse13Plan: false,
    nCourse14Plan: false,
  },
  statusCode: 0,
  error: null,
})

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/ContractInformation',
})
export default class ContractInformation extends VuexModule {
  // States
  private state_: StateContractInformation = getEmptyStateContractInformation()

  // Getters
  get result(): StateContractInformation {
    return this.state_
  }

  // Mutations

  @Mutation
  updateContractInformation(value: StateContractInformation) {
    this.state_ = value
  }

  static readonly MUTATION_NAME_UPDATE_STATE = 'updateContractInformation'

  // Actions

  @Action
  clear() {
    this.context.commit(ContractInformation.MUTATION_NAME_UPDATE_STATE, getEmptyStateContractInformation())
  }

  @Action
  async getContractionInformation(): Promise<boolean> {
    logger.trace('in: getContractionInformation')
    const api = new ApiTaCm010.Api()

    // API
    let hasError = true
    const result = await api.get<StateContractInformation>(
      {
        query: {},
      },
      // Success
      (data, status) => {
        const retData = {
          currentCourseId: data.currentCourseId,
          contractInfo: data.contractInfo,
          isContractInfo: data.isContractInfo,
          planTypes: getEmptyStateContractInformation().planTypes,
          coursePlanContractInfo: getEmptyStateContractInformation().coursePlanContractInfo,
          statusCode: status,
          error: null,
        }
        // 契約情報から各コースプラン情報を設定する
        if (data.contractInfo !== null) {
          data.contractInfo.forEach(elem => {
            retData.planTypes.add(PlanType.convertPlanType(elem.planId))
          })
        }
        // privateメソッドにしたかったが、TypeError: _this2.method is not a function となるため、ここで処理
        if (retData.planTypes.has(PlanType.JuniorTabletCourse)) {
          retData.coursePlanContractInfo.juniorTabletCourse = true
        }
        if (retData.planTypes.has(PlanType.TotalGuidancePlan)) {
          retData.coursePlanContractInfo.totalGuidancePlan = true
        }
        if (retData.planTypes.has(PlanType.CramSchoolLearningPlan)) {
          retData.coursePlanContractInfo.cramSchoolLearningPlan = true
        }
        if (retData.planTypes.has(PlanType.JuniorNCourse)) {
          retData.coursePlanContractInfo.juniorNCourse = true
        }
        if (retData.planTypes.has(PlanType.ACourseAPlan)) {
          retData.coursePlanContractInfo.aCourseAPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseBPlan)) {
          retData.coursePlanContractInfo.aCourseBPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseCPlan)) {
          retData.coursePlanContractInfo.aCourseCPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseDPlan)) {
          retData.coursePlanContractInfo.aCourseDPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseEPlan)) {
          retData.coursePlanContractInfo.aCourseEPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseFPlan)) {
          retData.coursePlanContractInfo.aCourseFPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseGPlan)) {
          retData.coursePlanContractInfo.aCourseGPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseHPlan)) {
          retData.coursePlanContractInfo.aCourseHPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseIPlan)) {
          retData.coursePlanContractInfo.aCourseIPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseJPlan)) {
          retData.coursePlanContractInfo.aCourseJPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseKPlan)) {
          retData.coursePlanContractInfo.aCourseKPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseLPlan)) {
          retData.coursePlanContractInfo.aCourseLPlan = true
        }
        if (retData.planTypes.has(PlanType.ACourseMPlan)) {
          retData.coursePlanContractInfo.aCourseMPlan = true
        }
        if (retData.planTypes.has(PlanType.NCourseWPlan)) {
          retData.coursePlanContractInfo.nCourseWPlan = true
        }
        if (retData.planTypes.has(PlanType.NCourseQPlan)) {
          retData.coursePlanContractInfo.nCourseQPlan = true
        }
        if (retData.planTypes.has(PlanType.NCourseVPlan)) {
          retData.coursePlanContractInfo.nCourseVPlan = true
        }
        if (retData.planTypes.has(PlanType.NCourseRPlan)) {
          retData.coursePlanContractInfo.nCourseRPlan = true
        }
        if (retData.planTypes.has(PlanType.JuniorNCourse13)) {
          retData.coursePlanContractInfo.nCourse13Plan = true
        }
        if (retData.planTypes.has(PlanType.JuniorNCourse14)) {
          retData.coursePlanContractInfo.nCourse14Plan = true
        }
        hasError = false
        return retData
      },
      // Error
      (data, status) => {
        hasError = true
        logger.error(status, data)
        return {
          currentCourseId: getEmptyStateContractInformation().currentCourseId,
          contractInfo: getEmptyStateContractInformation().contractInfo,
          isContractInfo: getEmptyStateContractInformation().isContractInfo,
          planTypes: getEmptyStateContractInformation().planTypes,
          coursePlanContractInfo: getEmptyStateContractInformation().coursePlanContractInfo,
          statusCode: status,
          error: {
            messageId: data.error.messageId,
            message: data.error.message,
          },
        }
      }
    )

    if (result) {
      const commitdata: StateContractInformation = getEmptyStateContractInformation()
      if (result) {
        commitdata.statusCode = result.statusCode
        commitdata.error = result.error
        if (commitdata.error === null) {
          commitdata.currentCourseId = result.currentCourseId
          commitdata.contractInfo = result.contractInfo
          commitdata.isContractInfo = result.isContractInfo
          commitdata.planTypes = result.planTypes
          commitdata.coursePlanContractInfo = result.coursePlanContractInfo
        }
      }
      this.context.commit(ContractInformation.MUTATION_NAME_UPDATE_STATE, commitdata)
    } else {
      hasError = true
    }

    return !hasError
  }
}
